/**
 * Auto-Ingest Toggle Component
 * Allows users to enable/disable automatic conversation ingestion to RAG
 */
import React, { useState, useEffect } from 'react';
import {
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import axios from 'axios';
import config from '../config';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = config.apiUrl;

export default function AutoIngestToggle() {
  const { t } = useTranslation(['common', 'chat']);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Load current status on mount
  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      
      // Guard: Check if token exists
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.get(
        `${API_BASE_URL}/users/settings/auto-ingest`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 10000, // 10s timeout
        }
      );

      setEnabled(response.data.enabled);
    } catch (err) {
      console.error('Failed to load auto-ingest status:', err);
      
      // Better error messages
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        setError(t('autoIngest.errorTimeout', { ns: 'chat' }));
      } else if (err.response?.status === 401) {
        setError(t('autoIngest.errorExpired', { ns: 'chat' }));
      } else if (err.response?.status >= 500) {
        setError(t('autoIngest.errorServer', { ns: 'chat' }));
      } else if (!navigator.onLine) {
        setError(t('autoIngest.errorNetwork', { ns: 'chat' }));
      } else {
        setError(t('autoIngest.errorLoad', { ns: 'chat' }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (event) => {
    const newValue = event.target.checked;
    const previousValue = enabled;
    
    // Optimistic update
    setEnabled(newValue);
    setUpdating(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      await axios.patch(
        `${API_BASE_URL}/users/settings/auto-ingest`,
        { enabled: newValue },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000, // 10s timeout
        }
      );

      setSuccessMessage(
        newValue
          ? t('autoIngest.successOn', { ns: 'chat' })
          : t('autoIngest.successOff', { ns: 'chat' })
      );
    } catch (err) {
      console.error('Failed to update auto-ingest:', err);
      
      // Revert on error
      setEnabled(previousValue);
      
      // Better error messages
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        setError(t('autoIngest.errorTimeout', { ns: 'chat' }));
      } else if (err.response?.status === 401) {
        setError(t('autoIngest.errorExpired', { ns: 'chat' }));
      } else if (err.response?.status === 403) {
        setError(t('autoIngest.errorForbidden', { ns: 'chat' }));
      } else if (err.response?.status >= 500) {
        setError(t('autoIngest.errorServer', { ns: 'chat' }));
      } else {
        setError(err.response?.data?.detail || t('autoIngest.errorUpdate', { ns: 'chat' }));
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleCloseSuccess = () => {
    setSuccessMessage('');
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1} sx={{ px: 2, py: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          {t('autoIngest.loading', { ns: 'chat' })}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ px: 2, py: 1 }}>
        <Tooltip
          title={
            enabled
              ? t('autoIngest.tooltipOn', { ns: 'chat' })
              : t('autoIngest.tooltipOff', { ns: 'chat' })
          }
          placement="right"
          arrow
        >
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={handleToggle}
                disabled={updating}
                color="primary"
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <AutoFixHighIcon fontSize="small" color={enabled ? 'primary' : 'action'} />
                <Typography variant="body2">
                  {updating ? t('autoIngest.updating', { ns: 'chat' }) : t('autoIngest.label', { ns: 'chat' })}
                </Typography>
              </Box>
            }
            sx={{
              m: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
              },
            }}
          />
        </Tooltip>

        {error && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {enabled && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, ml: 4 }}>
            {t('autoIngest.enabledNote', { ns: 'chat' })}
          </Typography>
        )}
      </Box>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" variant="filled">
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

